import {
  Box,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Stack,
  TypographyProps,
} from "@mui/material";
import { isNil } from "lodash-es";
import { ReactNode } from "react";

import DetailedViewPageHeader from "@/App/Layouts/PageHeader/DetailedViewPageHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import ViewContentBlockHeader from "@/App/Layouts/ViewContentBlockHeader";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppIconButton from "@/common/components/Button/AppIconButton";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import AppTypography from "@/common/components/Text/AppTypography";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { FileHelper } from "@/common/helpers/file";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { usePropagatedDeps } from "@/common/hooks/render/usePropagatedDeps";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, VehicleDto } from "@/core/api/generated/v1";

import Datetime from "../../../Datetime/Datetime";
import AppIcon from "../../../Icons/AppIcon";
import LoadingMenuItem from "../../../Menu/LoadingMenuItem";
import MenuWithTrigger from "../../../Menu/MenuWithTrigger";
import VehicleDamageList from "../../VehicleDamage/VehicleDamageList";
import { VehicleDamagePaginatedListPropagatedDepsMap } from "../../VehicleDamage/VehicleDamagePaginatedList";
import { VisualModelViewerOfVehicleActualState } from "../../VehicleVisualModel/Viewer/v2/Entity/VisualModelViewerOfVehicleActualState";
import VehicleBlock from "../VehicleBlock";
import VehicleLink from "../VehicleLink";

const defaultDisplayProps = {
  header: true,
  actions: true,
  vehicle: true,
  contract: true,
  breadcrumbs: true,
  /** For modal view.  */
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

const defaultHeaderProps = {
  withLink: false,
  title: undefined as ReactNode | undefined,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

export type VehicleActualStateProps = {
  vehicleId: string | undefined;
  vehicle?: VehicleDto | null;
  headerProps?: Partial<typeof defaultHeaderProps>;
  displayProps?: Partial<typeof defaultDisplayProps>;
  /** Mainly for modal view.  */
  onClose?: () => void;
};

export default function VehicleActualStateView({
  vehicleId,
  vehicle,
  displayProps = defaultDisplayProps,
  headerProps = defaultHeaderProps,
  onClose,
}: VehicleActualStateProps) {
  displayProps = { ...defaultDisplayProps, ...displayProps };
  headerProps = {
    ...defaultHeaderProps,
    ...headerProps,
  };

  const vehicleDamagePaginatedListPropagatedDeps =
    usePropagatedDeps<VehicleDamagePaginatedListPropagatedDepsMap>();

  const vehicleActualStateRequest = useApiRequest(
    apiClient.vehiclesApi.apiV1VehiclesVehicleIdActualStateGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: vehicleId!,
    },
    {
      // not sure why debounce was used here previously so leaving it in case it's important
      debouncedDeps: { deps: [vehicleId], wait: 300, options: { leading: false, trailing: true } },
      skip: !vehicleId,
    },
  );
  const vehicleActualState = vehicleActualStateRequest.data;

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb:
      (displayProps.breadcrumbs && {
        idValue: vehicleActualState?.vehicle?.id || "",
        newTitle: vehicleActualState?.vehicle?.localNumber || "",
      }) ||
      undefined,
  });

  return (
    <ViewLayout
      displayProps={displayProps}
      header={
        displayProps?.header && (
          <DetailedViewPageHeader
            title={
              headerProps?.title || (
                <>
                  <span>Actual state of </span>
                  <VehicleLink
                    isEnabled={!headerProps?.withLink}
                    entity={vehicleActualState?.vehicle}
                    inlineProps={{ withIcon: false }}
                  />
                </>
              )
            }
            titleProps={{
              to: headerProps?.withLink ? ROUTE_PATH.VEHICLE_ACTUAL_STATE(vehicleId) : undefined,
              linkProps: { disableTextDecoration: true },
              typographyProps: headerProps?.typographyProps,
            }}
            secondaryActions={
              displayProps.actions && (
                <Stack direction='row'>
                  <AppIconButton
                    tooltipProps={{ title: "Refresh" }}
                    onClick={() => {
                      vehicleActualStateRequest.refetch();
                      vehicleDamagePaginatedListPropagatedDeps.trigger("refetch");
                    }}
                  >
                    <AppIcon of='refresh' />
                  </AppIconButton>

                  <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                    <MenuWithTrigger
                      withAuthCloseOnClick={false}
                      trigger={
                        <AppIconButton>
                          <AppIcon of='moreVert' />
                        </AppIconButton>
                      }
                    >
                      {(params) => (
                        <LoadingMenuItem
                          key='downloadPdf'
                          onClick={async () => {
                            const downloadResult = await FileHelper.getDownloadFileApiResult(() =>
                              apiClient.vehiclesApi.apiV1VehiclesVehicleIdActualStatePdfGet(
                                {
                                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                                  vehicleId: vehicleId!,
                                },
                                {
                                  responseType: "blob",
                                },
                              ),
                            );
                            FileHelper.downloadBlobFromApiResult(downloadResult);
                            params.handleClose();
                          }}
                        >
                          <ListItemIcon>
                            <AppIcon of='download' fontSize='small' />
                          </ListItemIcon>
                          <ListItemText>Download PDF</ListItemText>
                        </LoadingMenuItem>
                      )}
                    </MenuWithTrigger>
                  </AuthorizedElement>
                </Stack>
              )
            }
            closeAction={
              displayProps.closeInHeader && onClose ? (
                <AppModalCloseIconButton onClick={onClose} />
              ) : undefined
            }
          />
        )
      }
    >
      <ViewContentBlock>
        {vehicleActualStateRequest.isLoading && <LinearProgress />}

        {displayProps?.vehicle && (
          <VehicleBlock
            variant='full'
            vehicle={vehicleActualState?.vehicle}
            withDetailsToggle
            withLink
          />
        )}

        <Stack gap={1}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(auto-fill, minmax(20%, 1fr))",
                md: "repeat(auto-fill, minmax(20%, 1fr))",
                lg: "repeat(auto-fill, minmax(20%, 1fr))",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile
              label='Mileage'
              iconOf='gauge'
              isEmpty={isNil(vehicleActualState?.vehicle?.mileage)}
            >
              <AppTypography>
                {vehicleActualState?.vehicle?.mileage?.mileage}{" "}
                <InlineApiEnumValue
                  type='MileageUnit'
                  value={vehicleActualState?.vehicle?.mileage?.unit}
                />
              </AppTypography>
            </InfoTile>

            <InfoTile
              label='Total damages'
              iconOf='damage'
              isEmpty={isNil(vehicleActualState?.vehicle?.totalDamagesCount)}
            >
              {vehicleActualState?.vehicle?.totalDamagesCount}
            </InfoTile>

            <InfoTile
              label='Actual damages'
              iconOf='damage'
              isEmpty={isNil(vehicleActualState?.vehicle?.actualDamagesCount)}
            >
              {vehicleActualState?.vehicle?.actualDamagesCount}
            </InfoTile>

            <InfoTile
              label='Repaired damages'
              iconOf='damage'
              isEmpty={isNil(vehicleActualState?.vehicle?.repairedDamagesCount)}
            >
              {vehicleActualState?.vehicle?.repairedDamagesCount}
            </InfoTile>

            <InfoTile
              label='Inactive damages'
              iconOf='damage'
              isEmpty={isNil(vehicleActualState?.vehicle?.inactiveDamagesCount)}
            >
              {vehicleActualState?.vehicle?.inactiveDamagesCount}
            </InfoTile>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(auto-fill, minmax(20%, 1fr))",
                md: "repeat(auto-fill, minmax(20%, 1fr))",
                lg: "repeat(auto-fill, minmax(20%, 1fr))",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile
              label='Last mileage update at'
              iconOf='gauge'
              isEmpty={isNil(vehicleActualState?.vehicle?.lastMileageUpdatedAt)}
            >
              <Datetime datetime={vehicleActualState?.vehicle?.lastMileageUpdatedAt} />
            </InfoTile>

            <InfoTile
              label='Last visual inspection at'
              iconOf='visualInspection'
              isEmpty={isNil(vehicleActualState?.vehicle?.lastVisualInspectionAt)}
            >
              <Datetime datetime={vehicleActualState?.vehicle?.lastVisualInspectionAt} />
            </InfoTile>

            <InfoTile
              label='Last damage detection at'
              iconOf='damageDetection'
              isEmpty={isNil(vehicleActualState?.vehicle?.lastDamageDetectionAt)}
            >
              <Datetime datetime={vehicleActualState?.vehicle?.lastDamageDetectionAt} />
            </InfoTile>

            <InfoTile
              label='Last accessory check at'
              iconOf='accessoryCheck'
              isEmpty={isNil(vehicleActualState?.vehicle?.lastAccessoryCheckAt)}
            >
              <Datetime datetime={vehicleActualState?.vehicle?.lastAccessoryCheckAt} />
            </InfoTile>
          </Box>
        </Stack>
      </ViewContentBlock>

      <ViewContentBlock>
        <ViewContentBlockHeader>Damages</ViewContentBlockHeader>

        {/* Visual models */}
        <Box>
          <VisualModelViewerOfVehicleActualState
            actualState={vehicleActualState}
            onDamageDeleted={() => {
              vehicleActualStateRequest.refetch();
            }}
          />
        </Box>

        <VehicleDamageList
          vehicleDamages={vehicleActualState?.damages || []}
          displayProps={{
            ...displayProps,
            vehicleColumn: displayProps?.vehicle,
            contractColumn: displayProps?.contract,
          }}
          dataTabularProps={{
            isLoading: vehicleActualStateRequest.isLoading,
          }}
          onUpdated={(newValue) => {
            newValue
              ? vehicleActualStateRequest.updateData((data) => {
                  data.damages = data.damages?.map((x) => (x.id == newValue.id ? newValue : x));
                })
              : vehicleActualStateRequest.refetch();
          }}
          onDeleted={(newValue) => {
            newValue
              ? vehicleActualStateRequest.updateData((data) => {
                  data.damages = data.damages?.filter((x) => x.id != newValue.id);
                })
              : vehicleActualStateRequest.refetch();
          }}
        />
      </ViewContentBlock>
    </ViewLayout>
  );
}
